import { MAX_SEARCH_KEYWORD_LENGTH } from '@/config';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslateSearchText } from '../features/analyze/api/translateSearchText';
import { useFilterConditionAtom } from '../features/analyze/atoms/filterCondition.atom';
import { TranslationInfo } from '../features/analyze/types';
import { useTranslateSearchEnabled } from '../features/analyze/hooks/useTranslateSearchEnabled';

export const useTranslateAndSearch = () => {
  const { t } = useTranslation();
  const [filterCondition, setFilterCondition] = useFilterConditionAtom();
  const translateSearchMutation = useTranslateSearchText();
  const [translationInfo, setTranslationInfo] =
    useState<TranslationInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 翻訳機能のON/OFF状態管理
  const [isTranslateSearchEnabled, setIsTranslateSearchEnabled] =
    useTranslateSearchEnabled();

  // 入力値の一元管理（常に表示用）
  const [inputValue, setInputValue] = useState(filterCondition.query ?? '');

  // 保存済みフィルタ適用などで atom の query が変わったとき、入力欄にも反映する
  useEffect(() => {
    setInputValue(filterCondition.query ?? '');
  }, [filterCondition.query]);

  const handleSearch = async (onSuccess: (translatedText: string) => void) => {
    setErrorMessage(null);

    const trimmedKeyword = inputValue?.trim();

    // 重複チェック: 現在の入力値と現在の検索条件を比較
    if (trimmedKeyword === filterCondition.query) {
      return;
    }

    if (!trimmedKeyword || trimmedKeyword.length === 0) {
      setFilterCondition((prev) => ({
        ...prev,
        query: undefined,
      }));
      setTranslationInfo(null);
      return;
    }

    if (trimmedKeyword.length > MAX_SEARCH_KEYWORD_LENGTH) {
      setErrorMessage(
        t(
          '検索ワードは{{maxSearchKeywordLength}}文字以内で入力してください。',
          { maxSearchKeywordLength: MAX_SEARCH_KEYWORD_LENGTH }
        )
      );
      return;
    }

    // 翻訳機能がOFFの場合は、そのまま検索
    if (!isTranslateSearchEnabled) {
      setFilterCondition((prev) => ({ ...prev, query: trimmedKeyword }));
      setTranslationInfo(null);
      onSuccess(trimmedKeyword); // 検索クエリを親コンポーネントに通知
      return;
    }

    // 翻訳実行
    try {
      const result = await translateSearchMutation.mutateAsync({
        text: trimmedKeyword,
      });

      if (!result?.translatedText) {
        setErrorMessage(t('翻訳中にエラーが発生しました'));
        return;
      }

      // 翻訳結果でinputValueを置き換える
      setInputValue(result.translatedText);
      setTranslationInfo({
        originalKeyword: trimmedKeyword,
        translatedKeyword: result.translatedText,
      });

      onSuccess(result.translatedText);
    } catch (error: unknown) {
      const defaultErrorMessage = t('翻訳中にエラーが発生しました');

      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message;
        setErrorMessage(errorMessage || defaultErrorMessage);

        return;
      }

      setErrorMessage(defaultErrorMessage);
    }
  };

  return {
    translationInfo,
    errorMessage,
    isTranslateSearchEnabled,
    filterCondition,
    setIsTranslateSearchEnabled,
    handleSearch,
    inputValue,
    setInputValue,
  };
};
